/**
 * The clock anchor (D-10).
 *
 * A device clock is not trustworthy: a phone that has been off all weekend can
 * be minutes out, and a punch stamped from it would arrive with a drift the
 * server flags. Every response carries `meta.serverTime`, so the client keeps
 * an offset from the last one it saw and renders elapsed time from that —
 * never from `Date.now()` alone.
 *
 * `performance.now()` is the monotonic base: it does not jump when the OS
 * corrects the wall clock mid-shift, which is exactly when a live "punched in
 * for 3:42" would otherwise leap.
 *
 * The anchor is in memory. Persisting it across restarts is the worker PWA's
 * job (IndexedDB `clockAnchor`), because only the punch queue needs it to
 * survive a cold start.
 */
export interface ClockAnchor {
  /** `serverTime - Date.now()` at the moment of the last response, in ms. */
  readonly offsetMs: number;
  /** `performance.now()` at that moment. */
  readonly monotonicBaseMs: number;
  /** `Date.now()` at that moment. */
  readonly wallClockBaseMs: number;
  /** The server instant itself, ISO-8601 UTC. */
  readonly lastServerContactAt: string;
}

let anchor: ClockAnchor | null = null;

function monotonicNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

/** Wire this to `configureApiClient({ onServerTime: recordServerTime })`. */
export function recordServerTime(serverTime: string): void {
  const serverMs = Date.parse(serverTime);
  if (Number.isNaN(serverMs)) return;
  const wallClockBaseMs = Date.now();
  anchor = {
    offsetMs: serverMs - wallClockBaseMs,
    monotonicBaseMs: monotonicNow(),
    wallClockBaseMs,
    lastServerContactAt: serverTime,
  };
}

export function getClockAnchor(): ClockAnchor | null {
  return anchor;
}

/** `null` until the first response — the UI must not invent a server time. */
export function clockOffsetMs(): number | null {
  return anchor?.offsetMs ?? null;
}

/**
 * The server's "now", corrected for device drift. Falls back to the device
 * clock before the first response, which is the only honest answer available.
 */
export function serverNow(): number {
  if (anchor === null) return Date.now();
  return anchor.wallClockBaseMs + anchor.offsetMs + (monotonicNow() - anchor.monotonicBaseMs);
}

/** Elapsed ms since a server-recorded instant, measured on the server's clock. */
export function elapsedSince(serverInstantIso: string): number {
  return Math.max(0, serverNow() - Date.parse(serverInstantIso));
}

/** Test-only. */
export function resetClockAnchor(): void {
  anchor = null;
}
